/**
 * Pure, unit-tested logic behind the Lidovi (leads) pipeline stepper
 * Unlike offers (an admin-configurable stage
 * catalog), lead stages are a FIXED 9-phase enum, so the ordered set and each
 * phase's kind live here in code — no server round-trip. Kept out of the React
 * pages so the ordering/colour rules are testable without a DOM, mirroring the
 * offersBoard.ts / offerPipeline.ts idiom.
 *
 * Colours reuse the offers palette decisions: ACTIVE phases sample the shared
 * sequential blue ramp (offerPipeline.sequentialStepColor), WON is the
 * reserved status-good green and LOST the reserved status-critical red —
 * always paired with a label, never colour alone.
 */
import type { LeadStage, LeadStageKind } from "../api/leads";
import { sequentialStepColor, STATUS_GOOD, STATUS_CRITICAL } from "./offerPipeline";

/** The 9 phases in exact pipeline order  — this IS the stepper order. */
export const LEAD_STAGES: LeadStage[] = [
  "NOV",
  "POKUSAN_KONTAKT",
  "LOS_TAJMING",
  "NEKVALIFIKOVAN",
  "U_OBRADI",
  "KVALIFIKOVAN",
  "PRILIKA",
  "DISKVALIFIKOVAN",
  "DOBIJEN",
];

const KIND_BY_STAGE: Record<LeadStage, LeadStageKind> = {
  NOV: "ACTIVE",
  POKUSAN_KONTAKT: "ACTIVE",
  LOS_TAJMING: "LOST",
  NEKVALIFIKOVAN: "LOST",
  U_OBRADI: "ACTIVE",
  KVALIFIKOVAN: "ACTIVE",
  PRILIKA: "ACTIVE",
  DISKVALIFIKOVAN: "LOST",
  DOBIJEN: "WON",
};

/** Kind (ACTIVE/WON/LOST) of a stage — drives colour and the convert gate. */
export function kindOf(stage: LeadStage): LeadStageKind {
  return KIND_BY_STAGE[stage];
}

/** ACTIVE phases in pipeline order — the blue-ramp track the stepper samples across. */
export const ACTIVE_STAGES: LeadStage[] = LEAD_STAGES.filter((s) => KIND_BY_STAGE[s] === "ACTIVE");

/**
 * Resolved colour token for a stage: WON green, LOST red, and each ACTIVE stage
 * its own step of the shared sequential blue ramp (sampled by its ordinal
 * position among the ACTIVE phases) — so the pipeline reads as distinct blue
 * steps advancing toward the green outcome, with the red off-ramps clearly set
 * apart. Reuses the offers colour decisions rather than inventing new hues.
 */
export function stageColor(stage: LeadStage): string {
  const kind = KIND_BY_STAGE[stage];
  if (kind === "WON") return STATUS_GOOD;
  if (kind === "LOST") return STATUS_CRITICAL;
  const idx = ACTIVE_STAGES.indexOf(stage);
  return sequentialStepColor(idx, ACTIVE_STAGES.length);
}

/** A WON lead (stage DOBIJEN) is the only one convertible into a customer. */
export function canConvert(stage: LeadStage): boolean {
  return stage === "DOBIJEN";
}

/**
 * Fades a stage colour (a `var(--…)` token or a `#rrggbb` hex) into a light
 * chip background on the current surface — same mix as offersBoard.ts `tint`,
 * duplicated locally so this pure module has no React import surface. Falls
 * back to a neutral surface tint for anything else.
 */
export function tint(color: string): string {
  if (!/^(var\(--[a-z0-9-]+\)|#[0-9a-f]{6})$/i.test(color)) return "var(--surface-2)";
  return `color-mix(in srgb, ${color} 12%, var(--surface))`;
}
